import React from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, TrendingUp } from "lucide-react";
import ImagePlaceholder from "@/components/ImagePlaceholder";
import Seo from "@/components/Seo";

const phases = [
  { n: "01", t: "Diagnose verstehen", d: "Zunächst klären, um welchen Toleranz-Bereich es sich handelt. Ein H2-Atemtest gibt Aufschluss." },
  { n: "02", t: "Fermentierte Produkte", d: "Kefir, Buttermilch, Joghurt und gereifter Käse enthalten wenig freie Lactose und sind ideal für den Start." },
  { n: "03", t: "Kleine Mengen zur Mahlzeit", d: "Milchprodukte immer als Teil einer Mahlzeit - nie auf leeren Magen. So verlangsamt sich die Magenpassage." },
  { n: "04", t: "Wöchentlich steigern", d: "Alle 5-7 Tage die Menge leicht erhöhen. Der Körper reagiert - Rückschläge sind normal." },
  { n: "05", t: "Milch schrittweise", d: "Frische Milch am Ende der Aufbauphase, immer in Kombination mit fester Nahrung." },
];

export default function Nutrition() {
  return (
    <div className="max-w-6xl mx-auto px-6 py-16 lg:py-24">
      <Seo
        title="Ernährung und Toleranz wiederherstellen"
        description="Wie sich Lactose-Toleranz durch strukturierte Ernährung in 12 Wochen wieder aufbauen lässt. 5 Phasen mit klaren Zielmengen."
        path="/ernaehrung"
        keywords="Lactose Toleranz aufbauen, Ernährungsplan Lactoseintoleranz, fermentierte Milchprodukte"
      />
      <div className="grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-7">
          <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold mb-3">Ernährung & Toleranz</div>
          <h1 className="text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">Wie sich Lactose-Toleranz wieder aufbauen lässt</h1>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            Der Darm ist trainierbar. Mit einem strukturierten Aufbau können viele Betroffene wieder deutlich mehr Milchprodukte genießen - ohne Verzicht auf Genuss und Alltagstauglichkeit.
          </p>

          <div className="mt-10 space-y-5">
            {phases.map((p) => (
              <Card key={p.n} className="p-6 rounded-2xl border-slate-100 flex gap-5 items-start card-lift">
                <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-700 font-bold flex items-center justify-center shrink-0">{p.n}</div>
                <div>
                  <div className="font-semibold text-slate-900 text-lg">{p.t}</div>
                  <div className="text-slate-600 mt-1 leading-relaxed">{p.d}</div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 lg:sticky lg:top-24 space-y-6">
          <ImagePlaceholder src="/recipe-photos/butter.webp" label="Milchprodukte" ratio="aspect-[4/5]" />
          <Card className="p-6 rounded-2xl border-emerald-100 bg-gradient-to-br from-emerald-50 to-white">
            <TrendingUp className="w-6 h-6 text-emerald-700 mb-3" />
            <div className="font-semibold text-slate-900">Der strukturierte 12-Wochen-Plan</div>
            <p className="text-sm text-slate-600 mt-2">Konkrete Rezepte, wöchentliche Steigerungen und klare Mengenangaben - alles in unserem Ernährungsplan.</p>
            <Link to="/paywall" className="inline-block mt-5" data-testid="nutrition-cta">
              <Button className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white btn-lift w-full">
                Plan freischalten <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </Card>
        </div>
      </div>
    </div>
  );
}
